import React, { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  DateTimePickerWithRange,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Icons,
} from '@ais-ui/ui-core';
import { CheckIcon } from '@radix-ui/react-icons';
import { PiChartPolarThin, PiStar, PiStarFill } from 'react-icons/pi';
import { XIcon } from 'lucide-react';
import { useGetSourceIdentifiers } from '../../../../../../../libs/api/src/lib/useCoverageApi';
import { SourceIdentifier } from '@ais-ui/api-spec/coverage';
import { cn, useTimeZoneContext } from '@ais-ui/utils';
import { useDateTimeFormat } from '../../../conversions/useDateFormat';
import { useLayers, useOpenLayersContext } from '@ais-ui/react-openlayers';
import { useCoverage } from '../../Coverage/CoverageContext';
import { usePermissions } from '../../../Permissions/PermissionsProvider';
import { useLocalization } from '@ais-ui/locale-management';
import { useLayerFavorites, LAYER_IDS } from '../../onMapStartup/LayersOnStart';

interface CoverageSourceIdentifierBadgeProps {
  sourceIdentifier: SourceIdentifier;
  onClick: (sourceIdentifier: SourceIdentifier) => void;
}

const CoverageSourceIdentifierBadge = ({
  sourceIdentifier,
  onClick,
}: CoverageSourceIdentifierBadgeProps) => {
  return (
    <div key={sourceIdentifier.id} className={cn('relative group px-1 py-0.5')}>
      <Badge
        onClick={() => onClick(sourceIdentifier)}
        className="space-x-2  hover:cursor-pointer"
      >
        <p>
          {sourceIdentifier.pssMmsi !== -1
            ? sourceIdentifier.pssMmsi
            : sourceIdentifier.sourceTag}
        </p>
        <XIcon className="size-4" />
      </Badge>
    </div>
  );
};

const SelectedSourceIdentifiers = () => {
  const { selectedSourceIdentifiers, onSourceIdentifierSelect } = useCoverage();

  // Calculate dynamic height based on content
  const calculatedHeight = useMemo(() => {
    const HEADER_HEIGHT = 32; // CommandGroup heading height
    const BADGE_HEIGHT = 29; // Approximate height of a badge row
    const EMPTY_STATE_HEIGHT = 24; // Height of empty state message
    const PADDING_AND_MARGINS = 16; // Additional padding/margins
    const MIN_HEIGHT = 44; // Minimum height to show at least some content
    const MAX_HEIGHT = 124; // Maximum height before scrolling

    let contentHeight = HEADER_HEIGHT + PADDING_AND_MARGINS;

    if (selectedSourceIdentifiers.length === 0) {
      // Add empty state height
      contentHeight += EMPTY_STATE_HEIGHT;
    } else {
      // Estimate rows needed based on badges (assuming ~3-4 badges per row)
      // You can adjust this calculation based on your actual badge widths
      const estimatedRows = Math.ceil(selectedSourceIdentifiers.length / 3);
      contentHeight += estimatedRows * BADGE_HEIGHT;
    }

    // Ensure height is within bounds
    return Math.max(MIN_HEIGHT, Math.min(contentHeight, MAX_HEIGHT));
  }, [selectedSourceIdentifiers.length]);

  return (
    <Command>
      <CommandList
        className="overflow-hidden"
        style={{ height: `${calculatedHeight}px` }}
      >
        <CommandGroup heading="Selected source identifiers">
          {selectedSourceIdentifiers.length === 0 ? (
            <div className="py-2 text-center text-sm">
              No selected source identifiers
            </div>
          ) : (
            <ScrollArea
              className="flex-grow p-0 overflow-y-auto pb-2"
              style={{ height: `${calculatedHeight - 32}px` }} // Subtract header height
            >
              <div className="flex flex-wrap gap-1 p-1">
                {selectedSourceIdentifiers.map((sourceIdentifier) => (
                  <CoverageSourceIdentifierBadge
                    key={sourceIdentifier.id}
                    sourceIdentifier={sourceIdentifier}
                    onClick={() => onSourceIdentifierSelect(sourceIdentifier)}
                  />
                ))}
              </div>
            </ScrollArea>
          )}
        </CommandGroup>
      </CommandList>
    </Command>
  );
};

interface SourceIdentifiersContentProps {
  isLoading: boolean;
  isError: boolean;
  sourceIdentifiers: SourceIdentifier[];
  onSourceIdentifierSelect: (sourceIdentifier: SourceIdentifier) => void;
  selectedSourceIdentifiers: SourceIdentifier[];
}

const SourceIdentifiersContent = ({
  isLoading,
  isError,
  sourceIdentifiers,
  onSourceIdentifierSelect,
  selectedSourceIdentifiers,
}: SourceIdentifiersContentProps) => {
  const { isSourceIdentifierLoading } = useCoverage();

  if (isLoading) {
    return <Icons.spinner className="animate-spin" />;
  }

  if (isError) {
    return (
      <div className="text-red-500 text-sm">
        Failed to load source identifiers. Please try again later.
      </div>
    );
  }

  if (sourceIdentifiers?.length) {
    return (
      <>
        {sourceIdentifiers.map((sourceIdentifier) => {
          const isItemLoading = isSourceIdentifierLoading(sourceIdentifier.id);
          const isSelected = selectedSourceIdentifiers.some(
            (selected) => selected.id === sourceIdentifier.id,
          );

          return (
            <CommandItem
              key={sourceIdentifier.id}
              value={
                sourceIdentifier.pssMmsi !== -1
                  ? sourceIdentifier.pssMmsi.toString()
                  : sourceIdentifier.sourceTag
              }
              onSelect={() =>
                !isItemLoading && onSourceIdentifierSelect(sourceIdentifier)
              }
              className={cn(isItemLoading && 'opacity-70 cursor-not-allowed')}
            >
              <div className="flex justify-between w-full items-center">
                <div>
                  {sourceIdentifier.pssMmsi !== -1
                    ? sourceIdentifier.pssMmsi
                    : sourceIdentifier.sourceTag}
                </div>
                <div className="flex items-center space-x-2">
                  {isItemLoading && (
                    <Icons.spinner className="size-3 animate-spin" />
                  )}
                  {!isItemLoading && isSelected && (
                    <CheckIcon className="size-4" />
                  )}
                </div>
              </div>
            </CommandItem>
          );
        })}
      </>
    );
  }

  return (
    <div className="text-muted-foreground text-sm">
      No source identifiers available.
    </div>
  );
};

interface SourceIdentifiersOnMapProps {
  sourceIdentifiers: SourceIdentifier[];
}

const SourceIdentifiersOnMapActions = ({
  sourceIdentifiers,
}: SourceIdentifiersOnMapProps) => {
  const { addLayer, doesLayerExist } = useLayers();
  const { addSourceIdentifiersToMap } = useCoverage();
  const { displayProjection, dataProjection } = useOpenLayersContext();
  const { isFavorite, isToggling, toggleFavorite } = useLayerFavorites();

  const isShownOnStartup = isFavorite(LAYER_IDS.COVERAGE);
  const isTogglingCoverage = isToggling(LAYER_IDS.COVERAGE);

  const onShowSourceIdentifiersOnStartUp = () => {
    toggleFavorite(LAYER_IDS.COVERAGE, 'coverage', 'Source identifiers on map');
  };

  return (
    <div className="flex items-center gap-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            id="add-source-identifiers-to-map-on-startup-button"
            variant="ghost"
            size={'icon'}
            className="size-7"
            disabled={isTogglingCoverage}
            onClick={onShowSourceIdentifiersOnStartUp}
          >
            {isTogglingCoverage ? (
              <Icons.spinner className="size-4 animate-spin" />
            ) : isShownOnStartup ? (
              <PiStarFill className="size-4 text-yellow-400" />
            ) : (
              <PiStar className="size-4" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {isShownOnStartup
            ? 'No longer show source identifiers on map on startup'
            : 'Add source identifiers to map on startup'}
        </TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            id="add-source-identifiers-to-map-button"
            variant="ghost"
            size={'icon'}
            className="size-7"
            onClick={() => {
              if (!doesLayerExist('source-identifiers')) {
                addSourceIdentifiersToMap(
                  addLayer,
                  sourceIdentifiers,
                  displayProjection,
                  dataProjection,
                );
              }
            }}
          >
            <PiChartPolarThin className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Add source identifiers to map</TooltipContent>
      </Tooltip>
    </div>
  );
};

interface CoverageButtonProps {
  coveragePermission: boolean;
  selectedSourceIdentifiers: SourceIdentifier[];
}

const CoverageButton = ({
  coveragePermission,
  selectedSourceIdentifiers,
}: CoverageButtonProps) => {
  const button = (
    <Button
      id="manage-coverage-button"
      size="icon"
      variant="outline"
      className="h-10 w-10 flex-shrink-0 flex-grow-0 relative"
      disabled={!coveragePermission}
    >
      <PiChartPolarThin className="size-5" />
      {selectedSourceIdentifiers.length > 0 && (
        <span className="absolute -top-1 -right-1.5 inline-flex items-center justify-center px-1 py-0.5 text-xs font-bold leading-none text-red-100 bg-primary rounded-full">
          {selectedSourceIdentifiers.length}
        </span>
      )}
    </Button>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {coveragePermission ? (
          <PopoverTrigger asChild>{button}</PopoverTrigger>
        ) : (
          <span className="inline-block">{button}</span>
        )}
      </TooltipTrigger>
      <TooltipContent>
        {coveragePermission
          ? 'Manage coverage'
          : 'You do not have permissions to coverage'}
      </TooltipContent>
    </Tooltip>
  );
};

export const Coverage = () => {
  const { messages } = useLocalization();
  const [open, setOpen] = useState(false);
  const permissions = usePermissions();
  const {
    selectedSourceIdentifiers,
    onSourceIdentifierSelect,
    coverageDate,
    setCoverageDate,
  } = useCoverage();

  const { timeZone } = useTimeZoneContext();
  const { userDateFormat } = useDateTimeFormat();

  const { data, isLoading, isError } = useGetSourceIdentifiers(
    permissions.coverage,
  );

  const sourceIdentifiers = useMemo(() => {
    if (data) {
      return data.sort((a, b) => a.sourceTag.localeCompare(b.sourceTag));
    }
    return [];
  }, [data]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <CoverageButton
        coveragePermission={permissions.coverage}
        selectedSourceIdentifiers={selectedSourceIdentifiers}
      ></CoverageButton>
      <PopoverContent className="w-96 p-0">
        <div className="flex items-center justify-between px-3 pt-3 pb-1">
          <h3 className="text-sm font-semibold">Coverage</h3>
          <SourceIdentifiersOnMapActions
            sourceIdentifiers={sourceIdentifiers}
          />
        </div>
        <div className="space-y-4 p-3 pt-2">
          <Command>
            <CommandGroup heading="Period">
              <DateTimePickerWithRange
                className="w-full"
                date={coverageDate}
                setDate={setCoverageDate}
                timeZone={timeZone}
                dateTimeFormat={userDateFormat}
                dateLimit="past"
              ></DateTimePickerWithRange>
            </CommandGroup>
          </Command>
          {selectedSourceIdentifiers.length > 0 && (
            <SelectedSourceIdentifiers />
          )}
          <Command>
            <CommandInput
              placeholder="Find source identifiers..."
              variant="bordered"
            />
            <ScrollArea className="h-[124px] flex-grow p-0 overflow-y-auto">
              {isLoading ? (
                <Icons.spinner className="animate-spin" />
              ) : isError ? (
                <div className="text-red-500 text-sm p-4">
                  Failed to load source identifiers. Please try again later.
                </div>
              ) : (
                <>
                  <CommandEmpty>
                    {messages.coverageNoSourceIdentifiersFound}
                  </CommandEmpty>
                  <CommandGroup heading="Source identifiers">
                    <SourceIdentifiersContent
                      isLoading={isLoading}
                      isError={isError}
                      sourceIdentifiers={sourceIdentifiers}
                      onSourceIdentifierSelect={onSourceIdentifierSelect}
                      selectedSourceIdentifiers={selectedSourceIdentifiers}
                    />
                  </CommandGroup>
                </>
              )}
            </ScrollArea>
          </Command>
        </div>
      </PopoverContent>
    </Popover>
  );
};
